"use client";

import { useState } from "react";
import { FlaskConical, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

interface TestOrderButtonProps {
  text: string;
  fontId: string;
  colorId: string;
  widthCm: number;
  heightCm: number;
  depthMm: number;
  fontsReady: boolean;
}

interface TestOrderResult {
  orderNumber: string;
  folderName: string;
  files: string[];
  errors: string[];
  mailSent: boolean;
}

/**
 * Internal-only (rendered only with ORDER_TEST_MODE=true): runs the
 * current design through the post-payment print pipeline without a Wix
 * checkout, and links the generated STL files for download.
 */
export function TestOrderButton({
  text,
  fontId,
  colorId,
  widthCm,
  heightCm,
  depthMm,
  fontsReady,
}: TestOrderButtonProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestOrderResult | null>(null);

  async function handleClick() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const response = await fetch("/api/orders/test", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, fontId, colorId, widthCm, heightCm, depthMm }),
      });
      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(body?.error ?? "Unbekannter Fehler.");
      }
      setResult(body);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Testbestellung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-2 rounded-md border border-dashed border-amber-400/60 p-3">
      <p className="text-xs font-semibold tracking-wide text-amber-300 uppercase">
        Testmodus
      </p>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={!fontsReady || !text.trim() || loading}
        onClick={handleClick}
      >
        {loading ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <FlaskConical className="size-4" />
        )}
        Druckdateien testweise erzeugen
      </Button>

      {error && <p className="text-sm text-red-400">{error}</p>}

      {result && (
        <div className="space-y-1 text-xs text-white/80">
          <p>
            {result.orderNumber}:{" "}
            {result.mailSent ? "Mail verschickt." : "Keine Mail (SMTP nicht konfiguriert)."}
          </p>
          {result.errors.map((e) => (
            <p key={e} className="text-red-400">{e}</p>
          ))}
          <ul className="max-h-40 space-y-0.5 overflow-y-auto">
            {result.files.map((file) => (
              <li key={file}>
                <a
                  className="inline-flex items-center gap-1 underline hover:text-white"
                  href={`/api/orders/test/files/${result.folderName}/${encodeURIComponent(file)}`}
                >
                  <Download className="size-3" />
                  {file}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
